import { destroyStores, refreshBearcod, refreshStocks, useLegalEntities } from '@gozoki/api'
import type { StoreType } from '@gozoki/api-types'
import { includesLowerCase } from '@gozoki/tools'
import { Button, IconButton, Menu, MenuItem, Paper, TextField, Typography } from '@mui/material'
import type { GridColDef, GridInitialState } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { RotateCw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api } from '~/lib/tuyau'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import { formatDateOnly } from '~/utils/types/formatDates'
import { storeTypeLabel } from '~/utils/types/toFrench'
import Page from '../../../components/Page'
import StoreCSVExportButton from '../../../components/buttons/StoreCSVExportButton'
import SupplyReportButton from '../../../components/buttons/SupplyReportButton'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import PlusIcon from '../../../components/icons/PlusIcon'
import { useRights } from '../../../utils/hooks'
import { fonts } from '../../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/stores/')({
  staticData: {
    permission: ['read', 'stores'],
  },
  component: StoresPage,
})
withRoutePermissions(Route)

const initialFilterState: GridInitialState = {
  filter: {
    filterModel: {
      items: [
        {
          field: 'storeType',
          operator: 'equals',
          value: 'BOUTIQUE', // La valeur à filtrer
        },
      ],
    },
  },
}

const promoCampaignColumnDefs: GridColDef[] = [
  {
    field: 'label',
    flex: 1,
    headerName: 'Nom',
  },
  {
    field: 'address',
    flex: 1,
    headerName: 'Adresse',
    renderCell: (field) => field.value.address,
  },
  {
    field: 'id',
    flex: 1,
    headerName: 'ID',
  },
  {
    field: 'storeType',
    flex: 1,
    headerName: 'Type de boutique',
    renderCell: (field) => storeTypeLabel[field.value as StoreType],
    type: 'string',
    valueFormatter: (field) => storeTypeLabel[field.value as StoreType],
  },
  {
    field: 'legalEntity',
    flex: 1,
    headerName: 'Entreprise légale',
  },
  {
    field: 'openingDate',
    flex: 1,
    headerName: "Date d'ouverture",
    valueFormatter: (params) => formatDateOnly(params.value as string),
  },
]

function StoresPage() {
  const isStockAdmin = useRights(['STOCK_ADMIN'])
  const isStockReader = useRights(['STOCK_READER'])
  const [storeSearch, setStoreSearch] = useState('')
  const navigate = useNavigate()

  const [storesSelection, setStoresSelection] = useState<number[]>([])
  const [refreshMenuAnchor, setRefreshMenuAnchor] = useState<null | HTMLElement>(null)

  const stores = useQuery(api.stores.index.queryOptions({}))

  const activeStores = useMemo(
    () =>
      stores.data
        ?.filter((store) => store.isOnline && store.storeType === 'BOUTIQUE')
        .map((store) => store.id) ?? [],
    [stores.data],
  )

  const storesToExport = useMemo(() => {
    if (storesSelection.length > 0) {
      return storesSelection
    }
    return activeStores
  }, [storesSelection, activeStores])

  const { data: legalEntities } = useLegalEntities()

  // ********** QUERY TO DATAGRID TRANSFORMATION *********** //

  const filteredStoreRows =
    stores.data?.filter((store) => includesLowerCase(store.label, storeSearch)) ?? []

  const storeRows = filteredStoreRows.map((store) => ({
    ...store,
    legalEntity:
      legalEntities?.find((legalEntity) => legalEntity.id === store.legalEntityId)?.name ?? 'N/A',
  }))

  // *********************** HANDLERS  ********************* //

  const deleteStoresMut = useMutation({
    mutationFn: destroyStores,
  })

  const refreshStockMut = useMutation({
    mutationFn: refreshStocks,
  })
  const refreshBearcodMut = useMutation({
    mutationFn: refreshBearcod,
  })

  const navigateAndUpdateCampaign = async () => {
    navigate({ search: { storeId: storesSelection[0] }, to: '/stores/new' })
  }

  const navigateStoreDetails = (id: number) =>
    navigate({ params: { storeId: id }, to: '/stores/$storeId' })

  const storesDisabled = storesSelection.length === 0 || deleteStoresMut.isPending

  return (
    <Page sx={fonts.roboto}>
      <div style={{ display: 'flex' }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Boutiques
        </Typography>
        {isStockReader && (
          <>
            <AuthorizedButtonLink
              variant="contained"
              sx={{ marginRight: '16px' }}
              to="/stores/new"
              disabled={!isStockAdmin}
            >
              <PlusIcon />
              Ajouter une boutique
            </AuthorizedButtonLink>
            <IconButton
              onClick={(e) => setRefreshMenuAnchor(e.currentTarget)}
              disabled={!isStockAdmin}
              sx={{
                '&.Mui-disabled': { backgroundColor: 'action.disabledBackground' },
                '&:hover': { backgroundColor: 'primary.dark' },
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                color: 'white',
                height: 40,
                width: 40,
              }}
            >
              <RotateCw />
            </IconButton>
            <Menu
              anchorEl={refreshMenuAnchor}
              open={Boolean(refreshMenuAnchor)}
              onClose={() => setRefreshMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  refreshStockMut.mutate()
                  setRefreshMenuAnchor(null)
                }}
                disabled={refreshStockMut.isPending}
              >
                Rafraîchir les stocks CheckPoint
              </MenuItem>
              <MenuItem
                onClick={() => {
                  refreshBearcodMut.mutate()
                  setRefreshMenuAnchor(null)
                }}
                disabled={refreshBearcodMut.isPending}
              >
                Rafraîchir les données Bearcod
              </MenuItem>
            </Menu>
          </>
        )}
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={storeSearch}
            onChange={(e) => setStoreSearch(e.target.value)}
          />
          <div style={{ flexGrow: 1 }} />

          {isStockReader && (
            <>
              <Button
                variant="contained"
                sx={{ height: '36px', marginRight: '16px' }}
                disabled={storesDisabled}
                onClick={navigateAndUpdateCampaign}
              >
                Modifier
              </Button>
              <StoreCSVExportButton
                selectedStores={storesToExport}
                sx={{ height: '36px', marginRight: '16px' }}
              />
              <SupplyReportButton />
            </>
          )}
        </div>
        <SimpleDataGrid
          columns={promoCampaignColumnDefs}
          rows={storeRows}
          loading={stores.isLoading}
          onRowSelectionModelChange={(newSelection) => setStoresSelection(newSelection as number[])}
          onRowClick={(row) => navigateStoreDetails(row.row.id)}
          initialState={initialFilterState}
        />
      </Paper>
    </Page>
  )
}

export default StoresPage
